import { useMemo, useState, type ReactElement } from 'react';
import {
  effortChoicesFor,
  effortLabel,
  type AiExecutionProfileView,
  type AiProfileApplyMode,
} from '../lib/ai-execution-profile';
import '../ai-execution-profile.css';

export interface AiExecutionProfileChange {
  readonly targetKind: 'overseer' | 'worker';
  readonly targetId: string;
  readonly offeringKey: string | null;
  readonly effortKey: string | null;
  readonly applyMode: AiProfileApplyMode;
}

export interface AiExecutionProfileApplyResult {
  readonly ok: boolean;
  readonly message: string;
}

const AUTO = '';
const MODEL_DEFAULT = '';

export function AiExecutionProfileStrip({
  profile,
  compact = false,
  onApply,
}: {
  readonly profile: AiExecutionProfileView;
  readonly compact?: boolean;
  /**
   * The one structured owner action (set_ai_execution_profile, AAL2). Absent: read-only.
   * No browser shell command and no second settings store.
   */
  readonly onApply?: (change: AiExecutionProfileChange) => Promise<AiExecutionProfileApplyResult>;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [offeringKey, setOfferingKey] = useState(profile.mode === 'Auto' ? AUTO : profile.configuredOfferingKey ?? AUTO);
  const [effortKey, setEffortKey] = useState(profile.configuredEffortKey ?? MODEL_DEFAULT);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<AiExecutionProfileApplyResult | null>(null);
  const applyMode: AiProfileApplyMode = profile.busy ? 'after_current_task' : 'now';
  const canApply = onApply !== undefined && profile.controllable;
  const allowsAuto = profile.targetKind === 'worker';

  const selectedRuntime = offeringKey === AUTO
    ? null
    : profile.modelOptions.find((option) => option.offeringKey === offeringKey)?.runtimeKey ?? null;
  const efforts = useMemo(() => effortChoicesFor(profile, selectedRuntime), [profile, selectedRuntime]);
  const effortSupported = effortKey === MODEL_DEFAULT || efforts.efforts.includes(effortKey);
  const selectedModelAvailable = (allowsAuto && offeringKey === AUTO)
    || profile.modelOptions.some((option) => option.offeringKey === offeringKey && option.available);

  const chooseModel = (value: string) => {
    setOfferingKey(value);
    setResult(null);
    // Effort belongs to one runtime; changing the model resets it rather than carrying an unsupported value.
    const runtime = value === AUTO ? null : profile.modelOptions.find((option) => option.offeringKey === value)?.runtimeKey ?? null;
    if (!effortChoicesFor(profile, runtime).efforts.includes(effortKey)) setEffortKey(MODEL_DEFAULT);
  };

  const submit = async () => {
    if (!canApply || !effortSupported || !selectedModelAvailable || pending) return;
    setPending(true);
    setResult(null);
    try {
      setResult(await onApply!({
        targetKind: profile.targetKind,
        targetId: profile.targetId,
        offeringKey: offeringKey === AUTO ? null : offeringKey,
        effortKey: offeringKey === AUTO || effortKey === MODEL_DEFAULT ? null : effortKey,
        applyMode,
      }));
    } finally {
      setPending(false);
    }
  };

  const status = profile.comparison === 'mismatch'
    ? { text: 'Mismatch', className: 'status status-failed' }
    : profile.comparison === 'pending'
      ? { text: 'Applying — waiting for the computer to report', className: 'status' }
      : profile.comparison === 'match'
        ? { text: 'Actual matches configured', className: 'status status-ok' }
        : profile.comparison === 'not_proven'
          ? { text: 'Runtime matches — resolved model not yet proven', className: 'status muted' }
          : profile.neverRun
            ? { text: 'Not yet proven', className: 'status muted' }
            : { text: 'Actual unknown', className: 'status muted' };

  return (
    <section className={compact ? 'ai-execution-profile compact stack tight' : 'ai-execution-profile stack tight'}
      aria-label={`${profile.title} AI execution profile`}
      data-ai-target={profile.targetKind}
      data-ai-mode={profile.mode.toLowerCase().replaceAll(' ', '_')}
      data-ai-comparison={profile.comparison}
      data-ai-mismatch={profile.mismatch ? 'true' : 'false'}>
      {profile.identityUnavailable ? (
        <strong>{profile.targetKind === 'overseer'
          ? 'Overseer identity unavailable / not configured'
          : 'AI identity unavailable / not configured'}</strong>
      ) : null}
      <small data-ai-line="configured"><strong>Configured:</strong> {profile.configuredLabel}</small>
      <small data-ai-line="actual"><strong>Actual:</strong> {profile.actualLabel}</small>
      <small>{profile.node} · {profile.source} · {profile.mode} · {profile.availability} · {profile.proof}</small>
      <small role="status" className={status.className}>{status.text}</small>
      {profile.selectedIsRecommended ? (
        <small data-ai-line="recommended" data-ai-recommended="selected"><strong>Recommended</strong> — the selected AI
          {profile.recommendationReason ? ` · ${profile.recommendationReason}` : ''}</small>
      ) : profile.recommendedLabel !== null ? (
        <small data-ai-line="recommended"><strong>Recommended:</strong> {profile.recommendedLabel}
          {profile.recommendationReason ? ` · ${profile.recommendationReason}` : ''}</small>
      ) : profile.recommendationReason !== null ? <small className="muted">{profile.recommendationReason}</small> : null}

      {(onApply !== undefined || !profile.controllable) && (
        <button type="button" className="link" onClick={() => setEditing((open) => !open)} aria-expanded={editing}>
          {editing ? 'Close AI settings' : 'Change'}
        </button>
      )}

      {editing && (
        <div className="stack tight" data-testid={`ai-profile-editor-${profile.targetKind}-${profile.targetId}`}>
          {!profile.controllable && profile.controlReason !== null && (
            <small className="muted" data-ai-blocked="true">{profile.controlReason}</small>
          )}

          <label className="stack tight">
            <span>Model</span>
            <select value={offeringKey} onChange={(event) => chooseModel(event.target.value)}
              disabled={!canApply || pending}>
              <option value={AUTO}>{allowsAuto ? 'Auto — Brunel chooses' : 'Choose an exact model'}</option>
              {profile.modelOptions.map((option) => (
                <option key={option.offeringKey} value={option.offeringKey} disabled={!option.available}>
                  {option.label}{option.reason ? ` — ${option.reason}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="stack tight">
            <span>Effort / Thinking</span>
            <select value={effortKey} onChange={(event) => { setEffortKey(event.target.value); setResult(null); }}
              disabled={!canApply || pending || offeringKey === AUTO || efforts.efforts.length === 0}>
              <option value={MODEL_DEFAULT}>Model default</option>
              {efforts.efforts.map((effort) => (
                <option key={effort} value={effort}>{effortLabel(effort, effort)}</option>
              ))}
            </select>
          </label>
          {offeringKey === AUTO
            ? <small className="muted">{allowsAuto
              ? 'Auto uses the model default effort; pin an exact model to choose effort.'
              : 'The Overseer requires one exact Planner Host model; Brunel will not substitute another model.'}</small>
            : efforts.reason !== null && <small className="muted">{efforts.reason}</small>}
          {offeringKey !== AUTO && efforts.efforts.length > 0 && !efforts.liveSwitch && (
            <small className="muted">This runtime reads model/effort only at launch: the healthy terminal is kept and the change applies at the next task.</small>
          )}

          {profile.busy && (
            <small className="muted">
              This Worker is busy. The change applies after the current task; it never switches mid-turn.
              To switch sooner, stop the run with the Worker&apos;s Stop control first.
            </small>
          )}

          <div className="row wrap">
            <button type="button" onClick={() => { void submit(); }}
              disabled={!canApply || pending || !effortSupported || !selectedModelAvailable}>
              {pending ? 'Applying…' : profile.busy ? 'Apply after current task' : 'Apply'}
            </button>
          </div>
          {result !== null && (
            <small role={result.ok ? 'status' : 'alert'} className={result.ok ? 'muted' : 'status status-failed'}>
              {result.message}
            </small>
          )}
        </div>
      )}
    </section>
  );
}
